import type { FilterGroupProps } from '../types'
import { Accordion } from '@sunwebgroup/ui-components/uds'
import { useCallback } from 'react'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createSearchFilterClickEvent } from '@/scopes/search/search/domain/analytics'
import { useMultiSelectFilter } from '@/scopes/search/search/domain/hooks'
import styles from './AirlineFilter.module.css'
import { DropdownMultiselect, useEditableDropdownMultiselect } from './DropdownMultiselect'
import { FormField, FormFieldControl } from './FormField'

export function AirlineFilter({ defaultOpen }: FilterGroupProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, options, selectedValues, setSelection } = useMultiSelectFilter('Airline')

  const handleEditFinish = useCallback(
    (newSelectedValues: string[]) => {
      const selectedNames = options
        .filter(option => newSelectedValues.includes(option.value))
        .map(option => option.name)
        .join(',')

      sendEvent(createSearchFilterClickEvent({
        element: 'checkbox',
        type: 'airline',
        result: selectedNames,
      }))
    },
    [options, sendEvent],
  )

  const multiselectDropdownProps = useEditableDropdownMultiselect({
    value: selectedValues,
    onValueChange: setSelection,
    onEditFinish: handleEditFinish,
  })

  if (!options.length)
    return null

  const label = intl.formatMessage({ id: 'airline_filter_label' })

  return (
    <Accordion.Item title={label} defaultOpen={defaultOpen}>
      <FormField as="div">
        <FormFieldControl data-qa="airline-filter">
          <DropdownMultiselect.Root
            disabled={isFetching}
            placeholder={intl.formatMessage({ id: 'airline_filter_placeholder' })}
            {...multiselectDropdownProps}
          >
            <DropdownMultiselect.Selection data-qa="airline-filter-selection">
              {options.map(({ value, name }) => (
                <DropdownMultiselect.SelectedValue key={value} value={value}>
                  {name}
                </DropdownMultiselect.SelectedValue>
              ))}
            </DropdownMultiselect.Selection>
            <DropdownMultiselect.Popover title={label} closeText={intl.formatMessage({ id: 'close' })}>
              <div className={styles.focusPadding}>
                {options.map(({ value, name, resultsCount }) => (
                  <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount} data-qa="airline-filter-option">
                    {name}
                  </DropdownMultiselect.CheckboxWithCounterOption>
                ))}
              </div>
              <DropdownMultiselect.ConfirmButton>
                {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
              </DropdownMultiselect.ConfirmButton>
            </DropdownMultiselect.Popover>
          </DropdownMultiselect.Root>
        </FormFieldControl>
      </FormField>
    </Accordion.Item>
  )
}
